/**
 * Adapted from PlayCanvas Engine's gsplat-sog-data.js at
 * d88289fb0c17be76ec4159974faf0d07d3582a6b (MIT License).
 */
import * as THREE from 'three'
import { GSplatData } from '@/plugins/gsplat/core/GSplatData'
import type {
  GSplatGraphicsAdapter,
  GSplatProcessingPassLeaf,
  GSplatProcessingPipelineLeaf,
  GSplatProcessingTargetLeaf,
  GSplatTextureLeaf,
} from '@/plugins/gsplat/graphics/GSplatGraphicsAdapter'
import sogCentersGLSL from '@/plugins/gsplat/shaders/unified/gsplatSogCenters.glsl'
import sogCentersWGSL from '@/plugins/gsplat/shaders/unified/gsplatSogCenters.wgsl'

const SH_C0 = 0.28209479177387814
const SH_COEFFICIENTS = Object.freeze([0, 3, 8, 15] as const)
const SOURCE_NAMES = Object.freeze([
  'means_l',
  'means_u',
  'quats',
  'scales',
  'sh0',
  'sh_labels',
  'sh_centroids',
] as const)

export type GSplatSogSourceName = (typeof SOURCE_NAMES)[number]

export interface GSplatSogMetaV1 {
  readonly version: 1
  readonly count: number
  readonly means: GSplatSogRange3
  readonly scales: GSplatSogRange3
  readonly quats: GSplatSogFiles
  readonly sh0: GSplatSogRange4
  readonly shN?: GSplatSogScalarRange
}

export interface GSplatSogMetaV2 {
  readonly version: 2
  readonly count: number
  readonly means: GSplatSogRange3
  readonly scales: GSplatSogCodebook
  readonly quats: GSplatSogFiles
  readonly sh0: GSplatSogCodebook
  readonly shN?: GSplatSogCodebook & { readonly bands?: number; readonly count?: number }
}

export type GSplatSogMeta = GSplatSogMetaV1 | GSplatSogMetaV2

export interface GSplatSogFiles {
  readonly files: readonly string[]
}

export interface GSplatSogRange3 extends GSplatSogFiles {
  readonly mins: readonly [number, number, number]
  readonly maxs: readonly [number, number, number]
}

export interface GSplatSogRange4 extends GSplatSogFiles {
  readonly mins: readonly [number, number, number, number]
  readonly maxs: readonly [number, number, number, number]
}

export interface GSplatSogScalarRange extends GSplatSogFiles {
  readonly mins: number
  readonly maxs: number
}

export interface GSplatSogCodebook extends GSplatSogFiles {
  readonly codebook: Array<number | null>
}

export interface GSplatSogTextureSource {
  readonly texture: GSplatTextureLeaf
  /** Browser decode 后的 RGBA8 snapshot；releaseTextureSources 后可丢弃。 */
  readonly pixels?: Uint8Array | Uint8ClampedArray | null
}

export interface GSplatSogSources {
  readonly means_l: GSplatSogTextureSource
  readonly means_u: GSplatSogTextureSource
  readonly quats: GSplatSogTextureSource
  readonly scales: GSplatSogTextureSource
  readonly sh0: GSplatSogTextureSource
  readonly sh_labels?: GSplatSogTextureSource
  readonly sh_centroids?: GSplatSogTextureSource
}

export interface GSplatSogDataOptions {
  readonly adapter: GSplatGraphicsAdapter
  readonly meta: GSplatSogMeta
  readonly sources: GSplatSogSources
  readonly url?: string
}

type SogSnapshot = Readonly<Partial<Record<GSplatSogSourceName, Uint8Array>>>

/** 冻结 PlayCanvas SOG V1/V2 CPU iterator 的 Three.js 适配。 */
export class GSplatSogIterator {
  private readonly coefficients: number

  constructor(
    private readonly data: GSplatSogData,
    private readonly snapshot: SogSnapshot,
    private readonly position?: THREE.Vector3,
    private readonly rotation?: THREE.Quaternion,
    private readonly scale?: THREE.Vector3,
    private readonly color?: THREE.Vector4,
    private readonly sh?: Float32Array
  ) {
    this.coefficients = SH_COEFFICIENTS[data.shBands]
    if (sh && sh.length < 45) {
      throw new Error(`GSplatSogIterator: SH 输出长度不足，expected>=45, actual=${sh.length}`)
    }
  }

  read(index: number): void {
    if (!Number.isSafeInteger(index) || index < 0 || index >= this.data.numSplats) {
      throw new Error(`GSplatSogIterator: 非法 splat index=${index}`)
    }
    const { meta } = this.data
    const offset = index * 4
    if (this.position) {
      const low = this.require('means_l')
      const high = this.require('means_u')
      this.position.set(
        mapMean(meta.means, low[offset], high[offset]),
        mapMean(meta.means, low[offset + 1], high[offset + 1], 1),
        mapMean(meta.means, low[offset + 2], high[offset + 2], 2)
      )
    }
    if (this.rotation) {
      const quats = this.require('quats')
      const a = (quats[offset] / 255 - 0.5) * Math.SQRT2
      const b = (quats[offset + 1] / 255 - 0.5) * Math.SQRT2
      const c = (quats[offset + 2] / 255 - 0.5) * Math.SQRT2
      const missing = Math.sqrt(Math.max(0, 1 - (a * a + b * b + c * c)))
      switch (quats[offset + 3] - 252) {
        case 0:
          this.rotation.set(a, b, c, missing)
          break
        case 1:
          this.rotation.set(missing, b, c, a)
          break
        case 2:
          this.rotation.set(b, missing, c, a)
          break
        case 3:
          this.rotation.set(b, c, missing, a)
          break
      }
    }
    if (this.scale) {
      const scales = this.require('scales')
      if (meta.version === 2) {
        this.scale.set(
          codebookValue(meta.scales.codebook, scales[offset], 'scales'),
          codebookValue(meta.scales.codebook, scales[offset + 1], 'scales'),
          codebookValue(meta.scales.codebook, scales[offset + 2], 'scales')
        )
      } else {
        this.scale.set(
          lerp(meta.scales.mins[0], meta.scales.maxs[0], scales[offset] / 255),
          lerp(meta.scales.mins[1], meta.scales.maxs[1], scales[offset + 1] / 255),
          lerp(meta.scales.mins[2], meta.scales.maxs[2], scales[offset + 2] / 255)
        )
      }
    }
    if (this.color) {
      const sh0 = this.require('sh0')
      if (meta.version === 2) {
        this.color.set(
          0.5 + codebookValue(meta.sh0.codebook, sh0[offset], 'sh0') * SH_C0,
          0.5 + codebookValue(meta.sh0.codebook, sh0[offset + 1], 'sh0') * SH_C0,
          0.5 + codebookValue(meta.sh0.codebook, sh0[offset + 2], 'sh0') * SH_C0,
          sh0[offset + 3] / 255
        )
      } else {
        const opacity = lerp(meta.sh0.mins[3], meta.sh0.maxs[3], sh0[offset + 3] / 255)
        this.color.set(
          0.5 + lerp(meta.sh0.mins[0], meta.sh0.maxs[0], sh0[offset] / 255) * SH_C0,
          0.5 + lerp(meta.sh0.mins[1], meta.sh0.maxs[1], sh0[offset + 1] / 255) * SH_C0,
          0.5 + lerp(meta.sh0.mins[2], meta.sh0.maxs[2], sh0[offset + 2] / 255) * SH_C0,
          stableSigmoid(opacity)
        )
      }
    }
    if (this.sh && this.coefficients > 0) {
      const labels = this.require('sh_labels')
      const centroids = this.require('sh_centroids')
      const palette = labels[offset] + (labels[offset + 1] << 8)
      const u = (palette % 64) * this.coefficients
      const v = Math.floor(palette / 64)
      const width = this.data.sh_centroids!.width
      for (let channel = 0; channel < 3; channel++) {
        for (let coefficient = 0; coefficient < this.coefficients; coefficient++) {
          const source = (u + coefficient) * 4 + channel + v * width * 4
          this.sh[channel * 15 + coefficient] =
            meta.version === 2
              ? codebookValue(meta.shN!.codebook, centroids[source], 'shN')
              : lerp(meta.shN!.mins, meta.shN!.maxs, centroids[source] / 255)
        }
      }
    }
  }

  private require(name: GSplatSogSourceName): Uint8Array {
    const value = this.snapshot[name]
    if (!value) throw new Error(`GSplatSogIterator: 缺少 CPU texture snapshot ${name}`)
    return value
  }
}

/** SOG JSON/bundle 的格式原生 Data owner。 */
export class GSplatSogData {
  readonly adapter: GSplatGraphicsAdapter
  readonly meta: GSplatSogMeta
  readonly numSplats: number
  readonly url: string
  readonly means_l: GSplatTextureLeaf
  readonly means_u: GSplatTextureLeaf
  readonly quats: GSplatTextureLeaf
  readonly scales: GSplatTextureLeaf
  readonly sh0: GSplatTextureLeaf
  readonly sh_labels: GSplatTextureLeaf | null
  readonly sh_centroids: GSplatTextureLeaf | null
  readonly shBands: 0 | 1 | 2 | 3
  codebookTexture: GSplatTextureLeaf | null = null

  private readonly sourcePixels = new Map<GSplatSogSourceName, Uint8Array | null>()
  private centersData: Float32Array | null = null
  private centersTransferred = false
  private codebookGeneration = -1
  private lifecycleGeneration = 1
  private destroyed = false
  private centersPromise: Promise<void> | null = null

  constructor(options: GSplatSogDataOptions) {
    validateMeta(options.meta)
    validateSources(options.adapter, options.meta, options.sources)
    this.adapter = options.adapter
    this.meta = options.meta
    this.numSplats = options.meta.count
    this.url = options.url ?? ''
    this.means_l = options.sources.means_l.texture
    this.means_u = options.sources.means_u.texture
    this.quats = options.sources.quats.texture
    this.scales = options.sources.scales.texture
    this.sh0 = options.sources.sh0.texture
    this.sh_labels = options.sources.sh_labels?.texture ?? null
    this.sh_centroids = options.sources.sh_centroids?.texture ?? null
    this.shBands = GSplatSogData.calcBands(this.sh_centroids?.width)
    for (const name of SOURCE_NAMES) {
      const source = options.sources[name]
      this.sourcePixels.set(name, source?.pixels ? copyBytes(source.pixels) : null)
    }
  }

  static calcBands(centroidsWidth?: number): 0 | 1 | 2 | 3 {
    return ({ 192: 1, 512: 2, 960: 3 } as const)[centroidsWidth as 192 | 512 | 960] ?? 0
  }

  get isSog(): boolean {
    return true
  }

  get preparedCodebookGeneration(): number {
    return this.codebookGeneration
  }

  isPreparedForGeneration(generation = this.adapter.generation): boolean {
    return (
      this.meta.version !== 2 || (!!this.codebookTexture && this.codebookGeneration === generation)
    )
  }

  getSourceTextures(includeCodebook = true): ReadonlyMap<string, GSplatTextureLeaf> {
    const result = new Map<string, GSplatTextureLeaf>([
      ['means_l', this.means_l],
      ['means_u', this.means_u],
      ['quats', this.quats],
      ['scales', this.scales],
      ['sh0', this.sh0],
    ])
    if (this.shBands > 0) {
      result.set('sh_labels', this.sh_labels!)
      result.set('sh_centroids', this.sh_centroids!)
    }
    if (includeCodebook && this.meta.version === 2 && this.codebookTexture) {
      result.set('sogCodebook', this.codebookTexture)
    }
    return result
  }

  createIter(
    position?: THREE.Vector3,
    rotation?: THREE.Quaternion,
    scale?: THREE.Vector3,
    color?: THREE.Vector4,
    sh?: Float32Array
  ): GSplatSogIterator {
    this.assertActive()
    this.patchCodebooks()
    const snapshot: Partial<Record<GSplatSogSourceName, Uint8Array>> = {}
    for (const name of SOURCE_NAMES) {
      const pixels = this.sourcePixels.get(name)
      if (pixels) snapshot[name] = pixels
    }
    return new GSplatSogIterator(this, snapshot, position, rotation, scale, color, sh)
  }

  calcAabb(result: THREE.Box3): void {
    const { mins, maxs } = this.meta.means
    result.min.set(mapSignedExp(mins[0]), mapSignedExp(mins[1]), mapSignedExp(mins[2]))
    result.max.set(mapSignedExp(maxs[0]), mapSignedExp(maxs[1]), mapSignedExp(maxs[2]))
  }

  calcFocalPoint(result: THREE.Vector3): void {
    const { mins, maxs } = this.meta.means
    result.set(
      (mapSignedExp(mins[0]) + mapSignedExp(maxs[0])) * 0.5,
      (mapSignedExp(mins[1]) + mapSignedExp(maxs[1])) * 0.5,
      (mapSignedExp(mins[2]) + mapSignedExp(maxs[2])) * 0.5
    )
  }

  getCenters(): Float32Array {
    this.assertActive()
    if (!this.centersData || this.centersTransferred) {
      throw new Error('GSplatSogData centers 尚未准备或已经转移')
    }
    const result = this.centersData
    this.centersData = null
    this.centersTransferred = true
    return result
  }

  prepareCodebook(): void {
    this.assertActive()
    this.patchCodebooks()
    if (this.meta.version !== 2) return
    const generation = this.adapter.generation
    if (this.codebookTexture && this.codebookGeneration === generation) return
    const pixels = new Float32Array(256 * 4)
    for (let index = 0; index < 256; index++) {
      pixels[index * 4] = this.meta.scales.codebook[index] as number
      pixels[index * 4 + 1] = this.meta.sh0.codebook[index] as number
      pixels[index * 4 + 2] = (this.meta.shN?.codebook[index] as number | undefined) ?? 0
    }
    const descriptor = {
      label: this.url ? `sogCodebook_${this.url}` : 'sogCodebook',
      width: 256,
      height: 1,
      format: 'rgba32float' as const,
      usages: ['sampled', 'copy-src', 'copy-dst'] as const,
      minFilter: 'nearest' as const,
      magFilter: 'nearest' as const,
      addressU: 'clamp-to-edge' as const,
      addressV: 'clamp-to-edge' as const,
    }
    this.adapter.validateTextureDescriptor(descriptor)
    const texture = this.adapter.createTexture(descriptor)
    try {
      this.adapter.updateTexture(texture, pixels)
    } catch (error) {
      texture.dispose()
      throw error
    }
    const previous = this.codebookTexture
    this.codebookTexture = texture
    this.codebookGeneration = generation
    previous?.dispose()
  }

  async prepareGpuData(signal?: AbortSignal): Promise<void> {
    this.prepareCodebook()
    await this.generateCenters(signal)
  }

  generateCenters(signal?: AbortSignal): Promise<void> {
    this.assertActive()
    throwIfAborted(signal)
    if (this.centersData || this.centersTransferred) return Promise.resolve()
    if (this.centersPromise) return this.centersPromise
    const lifecycleGeneration = this.lifecycleGeneration
    const adapterGeneration = this.adapter.generation
    const promise = this.generateCentersPass(lifecycleGeneration, adapterGeneration, signal)
    const tracked = promise.finally(() => {
      if (this.centersPromise === tracked) this.centersPromise = null
    })
    this.centersPromise = tracked
    return tracked
  }

  async decompress(signal?: AbortSignal): Promise<GSplatData> {
    this.assertActive()
    this.patchCodebooks()
    const lifecycleGeneration = this.lifecycleGeneration
    const adapterGeneration = this.adapter.generation
    const snapshot: Partial<Record<GSplatSogSourceName, Uint8Array>> = {}
    for (const name of SOURCE_NAMES.slice(0, this.shBands > 0 ? 7 : 5)) {
      snapshot[name] = await this.readSource(name, signal)
      this.assertAsyncCurrent(lifecycleGeneration, adapterGeneration, signal)
    }
    const members = [
      'x',
      'y',
      'z',
      'f_dc_0',
      'f_dc_1',
      'f_dc_2',
      'opacity',
      'scale_0',
      'scale_1',
      'scale_2',
      'rot_0',
      'rot_1',
      'rot_2',
      'rot_3',
    ]
    if (this.shBands > 0) {
      members.splice(
        members.indexOf('f_dc_0') + 1,
        0,
        ...Array.from({ length: 45 }, (_, index) => `f_rest_${index}`)
      )
    }
    const storage = Object.fromEntries(
      members.map((name) => [name, new Float32Array(this.numSplats)])
    ) as Record<string, Float32Array>
    const position = new THREE.Vector3()
    const rotation = new THREE.Quaternion()
    const scale = new THREE.Vector3()
    const color = new THREE.Vector4()
    const sh = this.shBands > 0 ? new Float32Array(45) : undefined
    const iter = new GSplatSogIterator(this, snapshot, position, rotation, scale, color, sh)
    for (let index = 0; index < this.numSplats; index++) {
      iter.read(index)
      storage.x[index] = position.x
      storage.y[index] = position.y
      storage.z[index] = position.z
      storage.rot_0[index] = rotation.w
      storage.rot_1[index] = rotation.x
      storage.rot_2[index] = rotation.y
      storage.rot_3[index] = rotation.z
      storage.scale_0[index] = scale.x
      storage.scale_1[index] = scale.y
      storage.scale_2[index] = scale.z
      storage.f_dc_0[index] = (color.x - 0.5) / SH_C0
      storage.f_dc_1[index] = (color.y - 0.5) / SH_C0
      storage.f_dc_2[index] = (color.z - 0.5) / SH_C0
      storage.opacity[index] = color.w <= 0 ? -40 : color.w >= 1 ? 40 : -Math.log(1 / color.w - 1)
      if (sh) {
        for (let coefficient = 0; coefficient < 45; coefficient++) {
          storage[`f_rest_${coefficient}`][index] = sh[coefficient]
        }
      }
    }
    this.assertAsyncCurrent(lifecycleGeneration, adapterGeneration, signal)
    return new GSplatData([
      {
        name: 'vertex',
        count: this.numSplats,
        properties: members.map((name) => ({
          name,
          type: 'float',
          byteSize: 4,
          storage: storage[name],
        })),
      },
    ])
  }

  releaseTextureSources(): void {
    this.assertActive()
    for (const name of SOURCE_NAMES) {
      this.sourcePixels.set(name, null)
      this.getSourceTexture(name)?.releaseSource?.()
    }
  }

  destroy(): void {
    if (this.destroyed) return
    this.destroyed = true
    this.lifecycleGeneration++
    this.centersData = null
    this.centersPromise = null
    this.sourcePixels.clear()
    const textures = new Set(this.getSourceTextures(false).values())
    for (const texture of textures) texture.dispose()
    this.codebookTexture?.dispose()
    this.codebookTexture = null
    this.codebookGeneration = -1
  }

  private async generateCentersPass(
    lifecycleGeneration: number,
    adapterGeneration: number,
    signal?: AbortSignal
  ): Promise<void> {
    if (this.generateCentersFromSourcePixels(lifecycleGeneration, adapterGeneration, signal)) {
      return
    }
    const { width, height } = this.means_l
    let texture: GSplatTextureLeaf | null = null
    let target: GSplatProcessingTargetLeaf | null = null
    let pipeline: GSplatProcessingPipelineLeaf | null = null
    let pass: GSplatProcessingPassLeaf | null = null
    try {
      const descriptor = {
        label: 'sogCentersTexture',
        width,
        height,
        format: 'rgba32uint' as const,
        usages: ['render-attachment', 'copy-src'] as const,
      }
      this.adapter.validateTextureDescriptor(descriptor)
      texture = this.adapter.createTexture(descriptor)
      target = this.adapter.createPrivateProcessingTarget({
        label: 'SogGenerateCenters:target',
        attachments: [texture],
      })
      const language = this.adapter.language
      const source = language === 'wgsl' ? sogCentersWGSL : sogCentersGLSL
      pipeline = this.adapter.createPrivateProcessingPipeline({
        label: 'SogGenerateCenters',
        key: `SogGenerateCenters:${language}`,
        language,
        processSource: source,
        inputDeclarations: '',
        readCode: '',
        outputDeclarations: '',
        inputStreams: [
          { name: 'means_l', format: 'rgba8unorm', storage: 'resource' },
          { name: 'means_u', format: 'rgba8unorm', storage: 'resource' },
        ],
        outputStreams: [{ name: 'sogCentersTexture', format: 'rgba32uint', storage: 'resource' }],
        outputTypes: ['vec4u'],
        defines: [],
      })
      pass = this.adapter.createPrivateProcessingPass({
        label: 'SogGenerateCenters:pass',
        target,
        pipeline,
      })
      await pass.execute({
        textureBindings: [
          { name: 'means_l', texture: this.means_l },
          { name: 'means_u', texture: this.means_u },
        ],
        parameters: [
          ['numSplats', this.numSplats],
          ['means_mins', [...this.meta.means.mins]],
          ['means_maxs', [...this.meta.means.maxs]],
        ],
        blendState: null,
      })
      this.assertAsyncCurrent(lifecycleGeneration, adapterGeneration, signal)
      const view = await this.adapter.readTexture(texture, signal)
      this.assertAsyncCurrent(lifecycleGeneration, adapterGeneration, signal)
      const bytes = copyViewBytes(view)
      const requiredBytes = width * height * 4 * Uint32Array.BYTES_PER_ELEMENT
      if (bytes.byteLength < requiredBytes) {
        throw new Error(
          `GSplatSogData center readback 长度不足，expected>=${requiredBytes}, actual=${bytes.byteLength}`
        )
      }
      const floats = new Float32Array(bytes.buffer, bytes.byteOffset, requiredBytes / 4)
      const centers = new Float32Array(this.numSplats * 3)
      for (let index = 0; index < this.numSplats; index++) {
        centers[index * 3] = floats[index * 4]
        centers[index * 3 + 1] = floats[index * 4 + 1]
        centers[index * 3 + 2] = floats[index * 4 + 2]
      }
      this.assertAsyncCurrent(lifecycleGeneration, adapterGeneration, signal)
      this.centersData = centers
    } finally {
      pass?.dispose()
      pipeline?.dispose()
      target?.dispose()
      texture?.dispose()
    }
  }

  /**
   * SogParser 已经保留 browser decode 后的 RGBA8 snapshot。优先从同一份字节按
   * PlayCanvas shader 的 low/high signed-exp 公式生成 centers，避免每个 LOD 节点
   * 再执行一次 GPU pass/readback；snapshot 已释放时仍回退到上游 GPU 路径。
   */
  private generateCentersFromSourcePixels(
    lifecycleGeneration: number,
    adapterGeneration: number,
    signal?: AbortSignal
  ): boolean {
    const low = this.sourcePixels.get('means_l')
    const high = this.sourcePixels.get('means_u')
    if (!low || !high) return false

    const requiredBytes = this.numSplats * 4
    if (low.byteLength < requiredBytes || high.byteLength < requiredBytes) {
      throw new Error(
        `GSplatSogData center snapshot 长度不足，expected>=${requiredBytes}, ` +
          `actual=${low.byteLength}/${high.byteLength}`
      )
    }

    this.assertAsyncCurrent(lifecycleGeneration, adapterGeneration, signal)
    const centers = new Float32Array(this.numSplats * 3)
    for (let index = 0; index < this.numSplats; index++) {
      const sourceOffset = index * 4
      const centerOffset = index * 3
      centers[centerOffset] = mapMean(this.meta.means, low[sourceOffset], high[sourceOffset])
      centers[centerOffset + 1] = mapMean(
        this.meta.means,
        low[sourceOffset + 1],
        high[sourceOffset + 1],
        1
      )
      centers[centerOffset + 2] = mapMean(
        this.meta.means,
        low[sourceOffset + 2],
        high[sourceOffset + 2],
        2
      )
    }
    this.assertAsyncCurrent(lifecycleGeneration, adapterGeneration, signal)
    this.centersData = centers
    return true
  }

  private async readSource(name: GSplatSogSourceName, signal?: AbortSignal): Promise<Uint8Array> {
    throwIfAborted(signal)
    const source = this.sourcePixels.get(name)
    if (source) return Promise.resolve(source.slice())
    const texture = this.getSourceTexture(name)
    if (!texture) throw new Error(`GSplatSogData: 缺少 source texture ${name}`)
    const view = await this.adapter.readTexture(texture, signal)
    const bytes = copyViewBytes(view)
    const expected = texture.width * texture.height * 4
    if (bytes.byteLength < expected) {
      throw new Error(
        `GSplatSogData ${name} readback 长度不足，expected>=${expected}, actual=${bytes.byteLength}`
      )
    }
    return bytes.slice(0, expected)
  }

  private getSourceTexture(name: GSplatSogSourceName): GSplatTextureLeaf | null {
    return this[name] as GSplatTextureLeaf | null
  }

  private patchCodebooks(): void {
    if (this.meta.version !== 2) return
    for (const [name, entry] of [
      ['scales', this.meta.scales],
      ['sh0', this.meta.sh0],
      ['shN', this.meta.shN],
    ] as const) {
      if (!entry) continue
      if (entry.codebook[0] === null) {
        entry.codebook[0] =
          (entry.codebook[1] as number) +
          ((entry.codebook[1] as number) - (entry.codebook[255] as number)) / 255
      }
      validateCodebook(name, entry.codebook, false)
    }
  }

  private assertAsyncCurrent(
    lifecycleGeneration: number,
    adapterGeneration: number,
    signal?: AbortSignal
  ): void {
    throwIfAborted(signal)
    if (this.destroyed || this.lifecycleGeneration !== lifecycleGeneration) {
      throw new Error('GSplatSogData async result 已因 destroy/generation 失效')
    }
    if (this.adapter.generation !== adapterGeneration) {
      throw new Error('GSplatSogData async result 已因 graphics adapter generation 失效')
    }
  }

  private assertActive(): void {
    if (this.destroyed) throw new Error('GSplatSogData 已销毁')
  }
}

function validateMeta(meta: GSplatSogMeta): void {
  if (
    (meta.version !== 1 && meta.version !== 2) ||
    !Number.isSafeInteger(meta.count) ||
    meta.count < 0
  ) {
    throw new Error(`GSplatSogData: 非法 meta version/count=${String(meta.version)}/${meta.count}`)
  }
  validateFiniteArray('means.mins', meta.means.mins, 3)
  validateFiniteArray('means.maxs', meta.means.maxs, 3)
  if (meta.version === 2) {
    validateCodebook('scales', meta.scales.codebook, true)
    validateCodebook('sh0', meta.sh0.codebook, true)
    if (meta.shN) validateCodebook('shN', meta.shN.codebook, true)
  } else {
    validateFiniteArray('scales.mins', meta.scales.mins, 3)
    validateFiniteArray('scales.maxs', meta.scales.maxs, 3)
    validateFiniteArray('sh0.mins', meta.sh0.mins, 4)
    validateFiniteArray('sh0.maxs', meta.sh0.maxs, 4)
    if (meta.shN && (!Number.isFinite(meta.shN.mins) || !Number.isFinite(meta.shN.maxs))) {
      throw new Error('GSplatSogData: shN min/max 必须为有限值')
    }
  }
}

function validateSources(
  adapter: GSplatGraphicsAdapter,
  meta: GSplatSogMeta,
  sources: GSplatSogSources
): void {
  const base = [sources.means_l, sources.means_u, sources.quats, sources.scales, sources.sh0]
  const dimensions = `${sources.means_l.texture.width}x${sources.means_l.texture.height}`
  const capacity = sources.means_l.texture.width * sources.means_l.texture.height
  if (!Number.isSafeInteger(capacity) || capacity < meta.count) {
    throw new Error(
      `GSplatSogData: means texture capacity 不足，capacity=${capacity}, count=${meta.count}`
    )
  }
  for (const source of base) {
    const texture = source.texture
    if (texture.format !== 'rgba8unorm') {
      throw new Error(`GSplatSogData: source texture ${texture.label} 必须为 rgba8unorm`)
    }
    if (`${texture.width}x${texture.height}` !== dimensions) {
      throw new Error(`GSplatSogData: base source texture 尺寸不一致 ${texture.label}`)
    }
    adapter.validateTextureDescriptor({
      label: texture.label,
      width: texture.width,
      height: texture.height,
      format: texture.format,
      usages: ['sampled', 'copy-src'],
    })
    validatePixels(source)
  }
  if (!!sources.sh_labels !== !!sources.sh_centroids) {
    throw new Error('GSplatSogData: sh_labels/sh_centroids 必须同时存在或同时缺失')
  }
  if (sources.sh_labels && sources.sh_centroids) {
    const labels = sources.sh_labels.texture
    const centroids = sources.sh_centroids.texture
    for (const source of [sources.sh_labels, sources.sh_centroids]) {
      const texture = source.texture
      if (texture.format !== 'rgba8unorm') {
        throw new Error(`GSplatSogData: SH source texture ${texture.label} 必须为 rgba8unorm`)
      }
      adapter.validateTextureDescriptor({
        label: texture.label,
        width: texture.width,
        height: texture.height,
        format: texture.format,
        usages: ['sampled', 'copy-src'],
      })
      validatePixels(source)
    }
    if (
      `${labels.width}x${labels.height}` !== dimensions ||
      labels.width * labels.height < meta.count
    ) {
      throw new Error('GSplatSogData: sh_labels texture 尺寸或 capacity 非法')
    }
    const bands = GSplatSogData.calcBands(centroids.width)
    if (bands === 0) throw new Error('GSplatSogData: 非法 SH centroid texture width')
    const maxLabels = centroids.height * 64
    if (maxLabels < 1 || maxLabels > 65536) {
      throw new Error(`GSplatSogData: SH centroid label capacity 非法，actual=${maxLabels}`)
    }
    if (!meta.shN) throw new Error('GSplatSogData: SH textures 存在但 metadata.shN 缺失')
  } else if (meta.shN) {
    throw new Error('GSplatSogData: metadata.shN 存在但 SH textures 缺失')
  }
}

function validatePixels(source: GSplatSogTextureSource): void {
  if (!source.pixels) return
  const expected = source.texture.width * source.texture.height * 4
  if (source.pixels.byteLength !== expected) {
    throw new Error(
      `GSplatSogData: ${source.texture.label} pixels 长度不匹配，expected=${expected}, actual=${source.pixels.byteLength}`
    )
  }
}

function validateCodebook(
  name: string,
  codebook: readonly (number | null)[],
  allowNullFirst: boolean
): void {
  if (codebook.length !== 256) {
    throw new Error(`GSplatSogData: ${name} codebook 长度必须为256，actual=${codebook.length}`)
  }
  for (let index = 0; index < codebook.length; index++) {
    if (index === 0 && allowNullFirst && codebook[index] === null) continue
    if (!Number.isFinite(codebook[index])) {
      throw new Error(`GSplatSogData: ${name} codebook[${index}] 必须为有限值`)
    }
  }
}

function validateFiniteArray(name: string, values: readonly number[], length: number): void {
  if (values.length !== length || values.some((value) => !Number.isFinite(value))) {
    throw new Error(`GSplatSogData: ${name} 必须包含 ${length} 个有限值`)
  }
}

function codebookValue(codebook: readonly (number | null)[], index: number, name: string): number {
  const value = codebook[index]
  if (!Number.isFinite(value))
    throw new Error(`GSplatSogIterator: 非法 ${name} codebook index=${index}`)
  return value as number
}

function mapMean(range: GSplatSogRange3, low: number, high: number, axis = 0): number {
  return mapSignedExp(lerp(range.mins[axis], range.maxs[axis], ((high << 8) + low) / 65535))
}

function mapSignedExp(value: number): number {
  return Math.sign(value) * (Math.exp(Math.abs(value)) - 1)
}

function lerp(left: number, right: number, amount: number): number {
  return left * (1 - amount) + right * amount
}

function stableSigmoid(value: number): number {
  if (value > 0) return 1 / (1 + Math.exp(-value))
  const exp = Math.exp(value)
  return exp / (1 + exp)
}

function copyBytes(value: Uint8Array | Uint8ClampedArray): Uint8Array {
  return new Uint8Array(value.buffer, value.byteOffset, value.byteLength).slice()
}

function copyViewBytes(value: ArrayBufferView): Uint8Array {
  return new Uint8Array(value.buffer, value.byteOffset, value.byteLength).slice()
}

function throwIfAborted(signal?: AbortSignal): void {
  if (signal?.aborted) throw new DOMException('GSplatSogData operation 已取消', 'AbortError')
}
